import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Shield, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

// SuperAdmin-toggle: växlar signeringsmetod (BankID vs PIN) per företag och
// kan sätta en initial PIN direkt.
export default function SigningMethodToggle({ companyId, onChanged }) {
  const [method, setMethod] = useState('bankid');
  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pendingMethod, setPendingMethod] = useState('');

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.saGetCompanySigningMethod(companyId);
        setMethod(res.data.signing_method);
        setHasPin(!!res.data.has_pin);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const applyMethod = async (newMethod, pin) => {
    setSaving(true);
    try {
      const payload = { signing_method: newMethod };
      if (newMethod === 'pin' && pin) payload.initial_pin = pin;
      await api.saSetCompanySigningMethod(companyId, payload);
      setMethod(newMethod);
      if (pin) setHasPin(true);
      toast.success(newMethod === 'pin' ? 'Bytt till PIN-signering' : 'Bytt till BankID-signering');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte uppdatera');
    } finally {
      setSaving(false);
      setShowPinDialog(false);
      setPinValue('');
    }
  };

  const handleSwitch = (target) => {
    if (target === method) return;
    if (target === 'pin' && !hasPin) {
      // Behöver en initial PIN
      setPendingMethod('pin');
      setShowPinDialog(true);
      return;
    }
    applyMethod(target, null);
  };

  const handleConfirmPin = () => {
    if (!/^\d{4,8}$/.test(pinValue)) {
      toast.error('PIN måste vara 4–8 siffror');
      return;
    }
    applyMethod(pendingMethod || 'pin', pinValue);
  };

  if (loading) return null;

  return (
    <div className="border-t border-border pt-4">
      <h4 className="text-sm font-bold text-foreground flex items-center gap-1.5 mb-3">
        <Shield size={14} className="text-[#E8613A]" /> Signeringsmetod
      </h4>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => handleSwitch('bankid')}
          disabled={saving}
          data-testid="sa-signing-bankid"
          className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition-all ${
            method === 'bankid'
              ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
              : 'border-border bg-muted/30 text-muted-foreground hover:border-emerald-200'
          }`}
        >
          <Shield size={14} />
          BankID (IDURA)
        </button>
        <button
          onClick={() => handleSwitch('pin')}
          disabled={saving}
          data-testid="sa-signing-pin"
          className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition-all ${
            method === 'pin'
              ? 'border-amber-500 bg-amber-50 text-amber-800 shadow-sm'
              : 'border-border bg-muted/30 text-muted-foreground hover:border-amber-200'
          }`}
        >
          <KeyRound size={14} />
          PIN-kod {hasPin && <span className="text-[10px] text-emerald-700">✓ satt</span>}
        </button>
      </div>
      {method === 'pin' && hasPin && (
        <button
          onClick={() => { setPendingMethod('pin'); setShowPinDialog(true); }}
          className="mt-2 text-xs text-[#E8613A] hover:underline"
          data-testid="sa-reset-pin"
        >
          Återställ PIN
        </button>
      )}

      {/* PIN input dialog */}
      {showPinDialog && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-3" onClick={() => !saving && setShowPinDialog(false)}>
          <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="sa-pin-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-2">
              <KeyRound size={16} className="text-[#E8613A]" /> Sätt PIN-kod
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Kunden får denna PIN via mejl och kan byta den senare under Inställningar → Fakturering.
            </p>
            <Label className="text-xs">PIN-kod (4–8 siffror) *</Label>
            <Input
              type="text"
              inputMode="numeric"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 mb-4 tracking-widest text-center text-lg font-mono"
              placeholder="1234"
              autoFocus
              data-testid="sa-pin-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPinDialog(false)} className="flex-1" disabled={saving}>Avbryt</Button>
              <Button onClick={handleConfirmPin} disabled={saving || pinValue.length < 4} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-pin-confirm">
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Bekräfta'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
